import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ViewChangesModal = ({ editRequest, onClose }) => {
    if (!editRequest) return null;

    const currentData = {
        firstName: editRequest.firstName,
        lastName: editRequest.lastName,
        phoneNumber: editRequest.phoneNumber,
        state: editRequest.state,
        city: editRequest.city,
        position: editRequest.position,
        bio: editRequest.bio,
    };
    const proposedData = JSON.parse(editRequest.editedData);
    const changedKeys = Object.keys(proposedData);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full">
                <h3 className="text-xl font-bold mb-4 text-brand-primary">Review Profile Changes</h3>
                <div className="space-y-2 text-sm">
                    {changedKeys.map(key => {
                        const oldValue = currentData[key] || 'N/A';
                        const newValue = proposedData[key] || 'N/A';
                        if (oldValue !== newValue) {
                            return (
                                <div key={key} className="grid grid-cols-3 gap-2 p-2 rounded-md bg-gray-50">
                                    <strong className="capitalize text-gray-600 col-span-1">{key.replace(/([A-Z])/g, ' $1')}:</strong>
                                    <span className="text-red-600 col-span-1 line-through">{oldValue}</span>
                                    <span className="text-green-600 col-span-1">{newValue}</span>
                                </div>
                            );
                        }
                        return null;
                    })}
                </div>
                <div className="mt-6 text-right">
                    <button onClick={onClose} className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded">Close</button>
                </div>
            </div>
        </div>
    );
};

export const ApprovalsDashboard = () => {
    const { token } = useAuth();
    const [pendingUsers, setPendingUsers] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);
    const [pendingEdits, setPendingEdits] = useState([]);
    const [isLoadingEdits, setIsLoadingEdits] = useState(true);
    const [selectedEdit, setSelectedEdit] = useState(null);
    const [error, setError] = useState(null);
    const [statusMessage, setStatusMessage] = useState('');

    const fetchData = async () => {
        setIsLoadingUsers(true);
        setIsLoadingEdits(true);
        setError(null);
        try {
            const [usersRes, editsRes] = await Promise.all([
                fetch('https://members.abssvss.com/members_api/get_pending_users.php', { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch('https://members.abssvss.com/members_api/get_pending_edits.php', { headers: { 'Authorization': `Bearer ${token}` } })
            ]);

            if (!usersRes.ok) throw new Error('Failed to fetch pending users.');
            const usersData = await usersRes.json();
            setPendingUsers(usersData);

            if (!editsRes.ok) throw new Error('Failed to fetch pending edits.');
            const editsData = await editsRes.json();
            setPendingEdits(editsData);

        } catch (e) {
            setError(e.message);
        } finally {
            setIsLoadingUsers(false);
            setIsLoadingEdits(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [token]);

    const handleUpdateStatus = async (userId, newStatus) => {
        setStatusMessage('Updating...');
        try {
            const response = await fetch('https://members.abssvss.com/members_api/update_user_status.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ userId, newStatus })
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message);
            setStatusMessage(result.message);
            setPendingUsers(currentUsers => currentUsers.filter(user => user.id !== userId));
        } catch (e) {
            setStatusMessage(`Error: ${e.message}`);
        }
    };

    const handleEditAction = async (editId, action) => {
        setStatusMessage('Processing request...');
        try {
            const response = await fetch('https://members.abssvss.com/members_api/approve_profile_edit.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ editId, action })
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message);
            setStatusMessage(result.message);
            setPendingEdits(currentEdits => currentEdits.filter(edit => edit.editId !== editId));
        } catch (e) {
            setStatusMessage(`Error: ${e.message}`);
        }
    };

    return (
        <>
            {selectedEdit && <ViewChangesModal editRequest={selectedEdit} onClose={() => setSelectedEdit(null)} />}
            <div className="space-y-8">
                {statusMessage && <p className="text-center text-blue-600">{statusMessage}</p>}
                <div>
                    <h2 className="text-2xl font-semibold text-gray-700 mb-4">Pending New Member Approvals</h2>
                    <div className="overflow-x-auto">
                        {isLoadingUsers ? <p>Loading...</p> : (
                             <table className="min-w-full bg-white border border-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                        <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                        <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
                                        <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {pendingUsers.length > 0 ? (
                                        pendingUsers.map(user => (
                                        <tr key={user.id}>
                                            <td className="py-4 px-6 whitespace-nowrap">{user.firstName} {user.lastName}</td>
                                            <td className="py-4 px-6 whitespace-nowrap">{user.email}</td>
                                            <td className="py-4 px-6 whitespace-nowrap">{user.state}</td>
                                            <td className="py-4 px-6 whitespace-nowrap">
                                                <button onClick={() => handleUpdateStatus(user.id, 'active')} className="bg-green-500 hover:bg-green-600 text-white font-bold py-1 px-3 rounded mr-2">Approve</button>
                                                <button onClick={() => handleUpdateStatus(user.id, 'rejected')} className="bg-red-500 hover:bg-red-600 text-white font-bold py-1 px-3 rounded">Reject</button>
                                            </td>
                                        </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="4" className="py-4 px-6 text-center text-gray-500">No pending approvals.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                <div>
                    <h2 className="text-2xl font-semibold text-gray-700 mb-4">Pending Profile Edit Approvals</h2>
                     <div className="overflow-x-auto">
                        {isLoadingEdits ? <p>Loading...</p> : (
                        <table className="min-w-full bg-white border border-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Requested</th>
                                <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                            {pendingEdits.length > 0 ? (
                                pendingEdits.map(edit => (
                                <tr key={edit.editId}>
                                    <td className="py-4 px-6 whitespace-nowrap">{edit.firstName} {edit.lastName}</td>
                                    <td className="py-4 px-6 whitespace-nowrap">{edit.email}</td>
                                    <td className="py-4 px-6 whitespace-nowrap">{new Date(edit.requestedAt).toLocaleDateString()}</td>
                                    <td className="py-4 px-6 whitespace-nowrap">
                                    <button onClick={() => setSelectedEdit(edit)} className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-1 px-3 rounded mr-2">View</button>
                                    <button onClick={() => handleEditAction(edit.editId, 'approve')} className="bg-green-500 hover:bg-green-600 text-white font-bold py-1 px-3 rounded mr-2">Approve</button>
                                    <button onClick={() => handleEditAction(edit.editId, 'reject')} className="bg-red-500 hover:bg-red-600 text-white font-bold py-1 px-3 rounded">Reject</button>
                                    </td>
                                </tr>
                                ))
                            ) : (
                                <tr><td colSpan="4" className="py-4 px-6 text-center text-gray-500">No pending edit requests.</td></tr>
                            )}
                            </tbody>
                        </table>
                        )}
                        {error && <p className="text-red-500 mt-2">Error fetching data: {error}</p>}
                    </div>
                </div>
            </div>
        </>
    );
};

const StatisticsDashboard = () => {
    const { token } = useAuth();
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await fetch('https://members.abssvss.com/members_api/get_dashboard_stats.php', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                if (!response.ok) throw new Error('Failed to fetch stats.');
                setStats(data);
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchStats();
    }, [token]);

    if (isLoading) return <p>Loading statistics...</p>;
    if (!stats) return <p>Could not load statistics.</p>;

    return (
        <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-100 p-6 rounded-lg text-center">
                    <h3 className="text-4xl font-bold text-blue-800">{stats.totalMembers}</h3>
                    <p className="text-blue-600 font-semibold">Total Members</p>
                </div>
                 <div className="bg-yellow-100 p-6 rounded-lg text-center">
                    <h3 className="text-4xl font-bold text-yellow-800">{stats.pendingMembers}</h3>
                    <p className="text-yellow-600 font-semibold">Pending Approvals</p>
                </div>
                 <div className="bg-orange-100 p-6 rounded-lg text-center">
                    <h3 className="text-4xl font-bold text-orange-800">{stats.pendingEdits}</h3>
                    <p className="text-orange-600 font-semibold">Pending Edits</p>
                </div>
            </div>
            <div>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Active Members by State</h2>
                <div className="bg-gray-50 p-4 rounded-lg">
                    <ul className="space-y-2">
                        {stats.membersByState.map(item => (
                            <li key={item.state} className="flex justify-between items-center p-2 rounded-md hover:bg-gray-100">
                                <span className="font-medium text-gray-700">{item.state}</span>
                                <span className="font-bold text-brand-primary">{item.count}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};


function Dashboard() {
    const { user, logout } = useAuth();
    const location = useLocation();
    
    const getLinkClass = (path) => {
        return location.pathname.includes(path)
            ? 'border-brand-accent text-brand-accent'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300';
    };

    return (
        <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-6xl space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold text-brand-primary">Admin Dashboard</h1>
                <button onClick={logout} className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded">Logout</button>
            </div>

            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                     <Link to="stats" className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${getLinkClass('stats')}`}>
                        Statistics
                    </Link>
                    <Link to="approvals" className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${getLinkClass('approvals')}`}>
                        Approvals
                    </Link>
                    <Link to="manage-members" className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${getLinkClass('manage-members')}`}>
                        Manage Members
                    </Link>
                    {/* Conditionally render Bulk Import tab for superadmins */}
                    {user?.role === 'superadmin' && (
                        <Link to="bulk-import" className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${getLinkClass('bulk-import')}`}>
                            Bulk Import
                        </Link>
                    )}
                </nav>
            </div>
            
            <div className="mt-4">
                <Outlet />
            </div>
        </div>
    );
}

export default Dashboard;

